<template>
  <div class="app-wrapper" :class="{ 'is-collapsed': isCollapsed || isMobile }">
    <!-- 移动端遮罩层 -->
    <div
      v-if="isMobile && !isCollapsed"
      class="sidebar-overlay"
      @click="toggleCollapse"
    ></div>

    <!-- 侧边栏 -->
    <div class="sidebar-container" :class="{ 'sidebar-visible': isMobile && sidebarVisible }">
      <!-- Logo区：点击 Logo 区域切换收起/展开（桌面端） -->
      <div
        class="sidebar-logo"
        :title="isCollapsed ? t('layout.expandMenu') : t('layout.collapseMenu')"
        @click="!isMobile && toggleCollapse()"
      >
        <div class="logo-content">
          <TwinStarLogo :size="30" class="logo-mark" />
          <span class="logo-title" v-if="!isCollapsed || (isMobile && sidebarVisible)">{{ t('layout.brandName') }}</span>
        </div>
      </div>

      <!-- 菜单区 -->
      <el-menu
        :default-active="activeMenu"
        class="sidebar-menu"
        :collapse="isMobile ? false : isCollapsed"
        :collapse-transition="false"
        router
      >
        <template v-if="shouldShowMenu">
          <DynamicMenu />
        </template>
        <div v-else class="menu-loading">
          <Icon name="Loader2" :size="20" class="is-loading" />
          <span>{{ t('layout.menuLoading') }}</span>
        </div>
      </el-menu>
    </div>

    <!-- 主内容区域 -->
    <div class="main-container">
      <!-- 顶部导航栏 -->
      <div class="navbar">
        <!-- 左侧：汉堡按钮 + 面包屑 -->
        <div class="navbar-left">
          <div class="hamburger-btn" v-if="isMobile" @click="toggleMobileSidebar">
            <Icon :name="sidebarVisible ? 'X' : 'Menu'" :size="20" />
          </div>
          <el-breadcrumb separator="/" class="breadcrumb">
            <el-breadcrumb-item :to="{ path: '/dashboard' }">{{ t('layout.home') }}</el-breadcrumb-item>
            <el-breadcrumb-item v-for="(item, index) in breadcrumbList" :key="index">
              {{ translateMenu(item) }}
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>

        <!-- 右侧操作区 -->
        <div class="navbar-right">
          <!-- 组织徽章 -->
          <div class="org-badge" v-if="orgDisplayName">
            <Icon name="Building" :size="14" />
            <span class="org-name">{{ orgDisplayName }}</span>
          </div>

          <!-- 菜单搜索 -->
          <MenuSearch />

          <!-- 主题切换 -->
          <ThemeSwitch />

          <!-- 语言切换 -->
          <LangSwitch />

          <!-- 消息通知 -->
          <NotificationBell />

          <!-- 用户下拉菜单 -->
          <el-dropdown trigger="click" @command="handleCommand">
            <div class="user-dropdown">
              <div class="user-avatar">
                <img v-if="userStore.avatar" :src="avatarDisplayUrl" class="user-avatar-img" />
                <Icon v-else name="User" :size="16" />
              </div>
              <span class="username">{{ username }}</span>
              <Icon name="ChevronDown" :size="14" />
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">
                  <Icon name="User" :size="16" />
                  <span>{{ t('layout.profile') }}</span>
                </el-dropdown-item>
                <el-dropdown-item command="settings">
                  <Icon name="Settings" :size="16" />
                  <span>{{ t('layout.settings') }}</span>
                </el-dropdown-item>
                <el-dropdown-item divided command="logout">
                  <Icon name="LogOut" :size="16" />
                  <span>{{ t('layout.logout') }}</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>

      <!-- 标签页栏 -->
      <TagsView v-if="settingsStore.showTagsView" />

      <!-- 页面内容 -->
      <div class="app-main">
        <router-view v-slot="{ Component, route }">
          <keep-alive :include="settingsStore.cachedViews">
            <component :is="patchComponentName(Component, route)" :key="route.path" />
          </keep-alive>
        </router-view>
      </div>
    </div>

    <!-- 水印覆盖层 -->
    <Watermark />

    <!-- AI智能助手侧边栏 -->
    <AiSidebar />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessageBox } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { usePermissionStore } from '@/stores/permission'
import { useSettingsStore } from '@/stores/settings'
import Icon from '@/components/Icon/index.vue'
import TwinStarLogo from '@/components/TwinStarLogo/index.vue'
import DynamicMenu from '@/components/DynamicMenu/index.vue'
import TagsView from '@/components/TagsView/index.vue'
import LangSwitch from '@/components/LangSwitch/index.vue'
import ThemeSwitch from '@/components/ThemeSwitch/index.vue'
import NotificationBell from '@/components/NotificationBell/index.vue'
import MenuSearch from '@/components/MenuSearch/index.vue'
import Watermark from '@/components/Watermark/index.vue'
import AiSidebar from '@/components/AiSidebar/index.vue'

// i18n
const { t } = useI18n()

// Store
const userStore = useUserStore()
const permissionStore = usePermissionStore()
const settingsStore = useSettingsStore()

// 响应式数据
const isCollapsed = ref(false)
const isMobile = ref(false)
const sidebarVisible = ref(false)

const checkMobile = () => {
  const wasMobile = isMobile.value
  isMobile.value = window.innerWidth < 768
  if (isMobile.value && !wasMobile) {
    isCollapsed.value = true
    sidebarVisible.value = false
  } else if (!isMobile.value && wasMobile) {
    sidebarVisible.value = false
  }
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile)
})

// 路由相关
const route = useRoute()
const router = useRouter()

/**
 * 将组件名补丁为路由名，使 keep-alive :include 能正确匹配
 * keep-alive 的 include 按组件 name 匹配，而 cachedViews 存储的是路由 name
 * 动态路由的路由名（如 Placement_1006）与组件名（如 MerchantPlacement）不一致
 * 通过将组件的 name 设置为路由 name 来对齐两者
 */
function patchComponentName(comp, route) {
  if (comp?.type && route.name) {
    comp.type.name = route.name
  }
  return comp
}

// 计算属性
const username = computed(() => userStore.displayName || '管理员')
const avatarDisplayUrl = computed(() => {
  const av = userStore.avatar
  if (!av) return ''
  if (av.startsWith('http')) return av
  const base = import.meta.env.VITE_API_BASE_URL || ''
  return base.replace(/\/api$/, '') + av
})
const activeMenu = computed(() => route.path)
const orgDisplayName = computed(() => userStore.orgDisplayName)
const shouldShowMenu = computed(() => true)

const breadcrumbList = computed(() => {
  const matched = route.matched.filter(item => item.meta && item.meta.title)
  return matched.map(item => item.meta.title)
})

// Translate menu title using i18n
const translateMenu = (title) => {
  const key = `menu.${title}`
  return t(key, title)
}

// 方法
const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value
  if (isMobile.value) {
    sidebarVisible.value = !isCollapsed.value
  }
}

const toggleMobileSidebar = () => {
  sidebarVisible.value = !sidebarVisible.value
  isCollapsed.value = !sidebarVisible.value
}

const handleCommand = async (command) => {
  switch (command) {
    case 'profile':
      router.push('/profile')
      break
    case 'settings':
      router.push('/profile')
      break
    case 'logout':
      await handleLogout()
      break
  }
}

const handleLogout = async () => {
  try {
    await ElMessageBox.confirm(
      t('layout.logoutConfirm'),
      t('layout.tip'),
      {
        confirmButtonText: t('common.confirm'),
        cancelButtonText: t('common.cancel'),
        type: 'warning',
      }
    )

    await userStore.logout()
    permissionStore.resetPermission()
    router.push('/login')
  } catch (error) {
    if (error !== 'cancel') {
      console.error('退出登录失败:', error)
    }
  }
}
</script>

<style lang="scss" scoped>
/* ============================================
   布局容器
   ============================================ */
.app-wrapper {
  display: grid;
  grid-template-columns: var(--sidebar-width) 1fr;
  height: 100vh;
  overflow: hidden;
  transition: grid-template-columns var(--transition-base);

  &.is-collapsed {
    grid-template-columns: var(--sidebar-collapsed-width) 1fr;
  }
}

/* ============================================
   侧边栏
   ============================================ */
.sidebar-container {
  height: 100vh;
  background: var(--bg-sidebar);
  overflow: hidden;
  position: relative;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--sidebar-divider);
}

/* Logo区 —— 点击切换侧栏收起/展开（桌面端） */
.sidebar-logo {
  height: var(--header-height);
  min-height: var(--header-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background: var(--bg-sidebar);
  border-bottom: 1px solid var(--sidebar-divider);
  transition: all var(--transition-base);
  flex-shrink: 0;

  @media (min-width: 769px) {
    cursor: pointer;

    &:hover {
      background: var(--sidebar-bg-hover);
    }
  }
}

.logo-content {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 10px;
}

.logo-mark {
  filter: drop-shadow(0 2px 10px rgba(56, 189, 248, 0.35));
}

.logo-title {
  color: white;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 菜单区 */
.sidebar-menu {
  flex: 1;
  border-right: none;
  overflow-y: auto;
  overflow-x: hidden;
  background: transparent;

  /* 滚动条样式 */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  /* Element Plus 菜单样式覆盖 */
  :deep(.el-menu-item),
  :deep(.el-sub-menu__title) {
    height: 44px;
    line-height: 44px;
    padding: 0 16px;
    color: var(--sidebar-text);
    font-size: var(--font-size-secondary);
    border-radius: 10px;
    margin: 1px 10px;
    transition: all var(--transition-base);

    &:hover {
      color: var(--sidebar-text-hover);
      background: var(--sidebar-bg-hover);
    }
  }

  :deep(.el-menu-item.is-active) {
    color: #fff;
    background: var(--grad-brand);
    border-radius: 10px;
    margin: 2px 10px;
    padding-left: 14px;
    font-weight: 600;
    box-shadow: 0 8px 20px -6px rgba(79, 70, 229, 0.45);
  }

  :deep(.el-sub-menu) {
    .el-sub-menu__title {
      &:hover {
        color: var(--sidebar-text-hover);
        background: var(--sidebar-bg-hover);
      }
    }

    /* 展开的子菜单容器加圆角，配合胶囊菜单项 */
    > .el-menu {
      background: rgba(0, 0, 0, 0.1);
    }

    /* 二级菜单区域 */
    .el-menu {

      /* 二级菜单项 */
      .el-menu-item {
        padding-left: 48px !important;
        background: transparent;
      }

      /* 二级目录标题 —— 与同级菜单项对齐 */
      > .el-sub-menu > .el-sub-menu__title {
        padding-left: 48px !important;
      }

      /* 三级菜单项 */
      > .el-sub-menu > .el-menu .el-menu-item {
        padding-left: 72px !important;
        background: transparent;
      }
    }
  }

  :deep(.el-icon) {
    color: inherit;
  }
}

/* 菜单加载状态 */
.menu-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: var(--sidebar-text);
  gap: 12px;

  .is-loading {
    animation: rotate 1s linear infinite;
  }

  span {
    font-size: var(--font-size-secondary);
    opacity: 0.7;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ============================================
   主容器
   ============================================ */
.main-container {
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 0;
  overflow: hidden;
  background: var(--bg-page);
}

/* ============================================
   顶部导航栏
   ============================================ */
.navbar {
  height: var(--header-height);
  min-height: var(--header-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--content-padding);
  background: var(--bg-header);
  border-bottom: 1px solid var(--border-light);
  position: relative;
  z-index: 1001;
  flex-shrink: 0;
}

.navbar-left {
  flex: 1;
  min-width: 0;
}

.breadcrumb {
  :deep(.el-breadcrumb__item) {
    .el-breadcrumb__inner {
      font-size: var(--font-size-secondary);
      color: var(--text-secondary);
      font-weight: 400;

      &:hover {
        color: var(--primary);
      }

      &.is-link {
        color: var(--text-secondary);
      }
    }

    &:last-child {
      .el-breadcrumb__inner {
        color: var(--text-regular);
      }
    }
  }

  :deep(.el-breadcrumb__separator) {
    color: var(--text-placeholder);
    margin: 0 8px;
  }
}

.navbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  margin-left: 16px;
}

/* 组织徽章 */
.org-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(37, 99, 235, 0.07);
  border: 1px solid rgba(37, 99, 235, 0.18);
  border-radius: var(--radius-base);
  transition: all var(--transition-base);

  .org-name {
    font-size: var(--font-size-secondary);
    font-weight: 500;
    color: var(--primary);
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: rgba(37, 99, 235, 0.12);
    border-color: rgba(37, 99, 235, 0.28);
  }
}

/* 用户下拉菜单 */
.user-dropdown {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  cursor: pointer;
  border-radius: var(--radius-base);
  transition: all var(--transition-base);

  &:hover {
    background: var(--bg-page);
  }
}

.user-avatar {
  width: 28px;
  height: 28px;
  background: var(--grad-brand);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  overflow: hidden;
}

.user-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.username {
  font-size: var(--font-size-secondary);
  color: var(--text-regular);
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 下拉菜单样式 */
:deep(.el-dropdown-menu) {
  padding: 8px 0;
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-lg);
  border-radius: var(--radius-base);

  .el-dropdown-menu__item {
    padding: 8px 16px;
    font-size: var(--font-size-secondary);
    color: var(--text-regular);
    display: flex;
    align-items: center;
    gap: 10px;

    &:hover {
      background: var(--bg-page);
      color: var(--primary);
    }

    &.is-divided {
      border-top: 1px solid var(--border-lighter);
      margin-top: 4px;
      padding-top: 12px;
    }
  }
}

/* ============================================
   页面内容区
   ============================================ */
.app-main {
  flex: 1;
  padding: var(--content-padding);
  overflow-y: auto;

  /* 滚动条样式 */
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-base);
    border-radius: 3px;

    &:hover {
      background: var(--text-placeholder);
    }
  }
}

/* ============================================
   过渡动画
   ============================================ */
.fade-transform-enter-active,
.fade-transform-leave-active {
  transition: all var(--transition-base);
}

.fade-transform-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-transform-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ============================================
   响应式设计
   ============================================ */

/* 汉堡菜单按钮（仅移动端显示） */
.hamburger-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  cursor: pointer;
  border-radius: var(--radius-base);
  color: var(--text-regular);
  transition: all var(--transition-base);
  margin-right: 8px;
  flex-shrink: 0;

  &:hover {
    background: var(--bg-page);
    color: var(--primary);
  }
}

/* 移动端遮罩层 */
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1010;
  transition: opacity var(--transition-base);
}

@media (max-width: 768px) {
  .app-wrapper {
    grid-template-columns: 1fr !important;
  }

  .sidebar-container {
    position: fixed;
    top: 0;
    left: 0;
    width: var(--sidebar-width) !important;
    height: 100vh;
    z-index: 1020;
    transform: translateX(-100%);
    transition: transform var(--transition-base);

    &.sidebar-visible {
      transform: translateX(0);
    }
  }

  .navbar {
    padding: 0 12px;
  }

  .app-main {
    padding: 12px;
  }

  .breadcrumb {
    display: none;
  }

  .org-badge {
    .org-name {
      max-width: 80px;
      font-size: 12px;
    }
  }

  .username {
    display: none;
  }
}

/* 折叠状态样式调整（移动端侧栏隐藏时） */
.app-wrapper.is-collapsed {
  .sidebar-logo {
    .logo-title {
      display: none;
    }
  }
}
</style>
